import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/store';
import localconfig from '@/config/local-config.json';
import { fetchUser, clearUser } from '@/store/slices/userSlice';
import { fetchUserPermissions } from '@/store/slices/userPermissionSlice';

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading: authLoading, user: auth0User,
    getAccessTokenSilently, getIdTokenClaims
   } = useAuth0();
  const { data: userData, loading: userLoading, fetched, status } = useAppSelector((state: RootState) => state.user);

  useEffect(() => {
    let cancelled = false;
    async function fetchUserData() {
        // Only fetch if authenticated and haven't fetched yet
        if (isAuthenticated && !authLoading && status === 'idle'
            && !fetched
        ) {
            const idToken = await getIdTokenClaims();
            const accessToken = await getAccessTokenSilently();
            console.log('ID Token Claims:', idToken);
            const action = await dispatch(
                fetchUser({ 
                    idTokenProvider: () => getIdTokenClaims(),
                    accessTokenProvider: () => getAccessTokenSilently() 
                })
            );
            if (!cancelled && fetchUser.fulfilled.match(action)) {
                const apiUser = action.payload;
                console.log('UserProvider: fetched user data', apiUser);
                // lets fetch user permissions
                let accessTokenValue = import.meta.env.DEV ? localconfig.accessToken : accessToken;
                console.log('UserProvider: accesstoken', accessToken, apiUser.id);
                const permaction = await dispatch(
                    fetchUserPermissions({ 
                        userId: apiUser.id,
                        accessToken: accessTokenValue
                    })
                );
                
                if (fetchUserPermissions.rejected.match(permaction)) {
                  console.error('UserProvider: Permissions fetch failed:', permaction.error);
                } else {
                  console.log('UserProvider: Permissions fetched successfully:', permaction.payload);
                }

            } else {
                console.error('UserProvider: failed to fetch user data');
                dispatch(clearUser());
            }
            
        } else {
            // dispatch(clearUser());
        }
    }
    fetchUserData();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading, dispatch, auth0User]);

  // Optional: Show loading state while user data is being fetched
//   if (authLoading || (isAuthenticated && userLoading && !userData)) {
//     return (
//       <div style={{ 
//         display: 'flex', 
//         justifyContent: 'center', 
//         alignItems: 'center', 
//         height: '100vh' 
//       }}>
//         Loading user data...
//       </div>
//     );
//   }

  return <>{children}</>;
};